// ============================================================
// Level Definitions — 5 turtle-themed puzzle levels
// ============================================================
// All coordinates are in "world space" pixels.
// The visible viewport is 1280 × 720.
// Levels can be wider/taller than the viewport (camera follows Tank).

/**
 * Platform spec: { x, y, w, h, material?, color?, moving? }
 *   x, y = centre of platform
 *   moving: { axis:'x'|'y', range, speed }
 *
 * Hazard spec: { x, y, w, h, type:'lava'|'spike'|'jellyfish'|'boulder' }
 *
 * Goal spec: { x, y, type:'star'|'pearls'|'exit', count? }
 *
 * NPC spec: { x, y, type, dialog? }
 *
 * Water: { x, y, w, h }
 *
 * hints: shown in the word-input panel
 */

import { COLOR } from '../constants.js';

// Helper: create a flat platform
const plat = (x, y, w, h, opts = {}) => ({ x, y, w, h, ...opts });

export const LEVELS = [

  // ────────────────────────────────────────────────────────
  // LEVEL 1 – Shell Beach  ⭐  (Tutorial)
  // ────────────────────────────────────────────────────────
  {
    id: 1,
    name: 'Shell Beach',
    subtitle: 'Learn the magic of words!',
    stars: 1,
    worldW: 2400,
    worldH: 720,
    background: 'beach',
    music: 'beach',

    start: { x: 140, y: 540 },

    platforms: [
      // Ground sections with a gap
      plat(400,  660, 800,  40, { material: 'sand',  color: 0xF5DEB3 }),
      plat(1400, 660, 800,  40, { material: 'sand',  color: 0xF5DEB3 }),
      plat(2200, 660, 400,  40, { material: 'sand',  color: 0xF5DEB3 }),

      // Raised ledge with the goal
      plat(2100, 500,  240, 30, { material: 'stone', color: 0x95A5A6 }),

      // Floating stepping stone
      plat(950, 510, 100, 25, { material: 'stone', color: 0x95A5A6 }),
    ],

    water: [],
    windZones: [],

    hazards: [],

    goal: { x: 2100, y: 460, type: 'star' },

    npcs: [
      {
        x: 300, y: 590,
        type: 'turtle',
        dialog: [
          "Hi! I'm Sandy! Type words to spawn objects!",
          "Try typing: ladder, bridge, or trampoline",
          "Press Tab to open the word box!",
        ],
      },
    ],

    hints: ['ladder', 'bridge', 'trampoline', 'balloon', 'box'],
    unlockMessage: 'You crossed Shell Beach! Words have power!',
  },

  // ────────────────────────────────────────────────────────
  // LEVEL 2 – Coral Reef  ⭐⭐  (Water & buoyancy)
  // ────────────────────────────────────────────────────────
  {
    id: 2,
    name: 'Coral Reef',
    subtitle: 'Dive deep or float high!',
    stars: 2,
    worldW: 2600,
    worldH: 960,
    background: 'reef',
    music: 'underwater',

    start: { x: 120, y: 360 },

    platforms: [
      // Entry platform
      plat(200,  400, 300, 30, { material: 'stone',  color: 0x607D8B }),
      // Coral islands
      plat(700,  340, 160, 30, { material: 'coral',  color: 0xFF7043 }),
      plat(1100, 280, 160, 30, { material: 'coral',  color: 0xFF7043 }),
      plat(1550, 200, 200, 30, { material: 'coral',  color: 0xFF7043 }),
      plat(1950, 310, 200, 30, { material: 'coral',  color: 0xFF7043 }),
      // Deep reef floor
      plat(1300, 920, 2600, 30, { material: 'stone', color: 0x546E7A }),
      // Exit platform
      plat(2440, 350, 300, 30, { material: 'stone',  color: 0x607D8B }),
    ],

    water: [
      { x: 0, y: 480, w: 2600, h: 480 },
    ],

    windZones: [],

    hazards: [
      { x: 900,  y: 680, w: 60, h: 60, type: 'jellyfish' },
      { x: 1200, y: 720, w: 60, h: 60, type: 'jellyfish' },
      { x: 1700, y: 700, w: 60, h: 60, type: 'jellyfish' },
    ],

    goal: { x: 2440, y: 300, type: 'star' },

    // 3 pearls to collect (optional side goal)
    collectibles: [
      { x: 700,  y: 290, type: 'pearl' },
      { x: 1100, y: 230, type: 'pearl' },
      { x: 1950, y: 260, type: 'pearl' },
    ],

    npcs: [
      {
        x: 200, y: 350,
        type: 'turtle',
        dialog: [
          "The reef is flooded! Watch out for jellyfish!",
          "Try: boat, raft, or balloon to cross the water.",
          "Collect all 3 pearls for a bonus!",
        ],
      },
    ],

    hints: ['boat', 'raft', 'balloon', 'fish', 'bridge'],
    unlockMessage: 'You navigated the Coral Reef!',
  },

  // ────────────────────────────────────────────────────────
  // LEVEL 3 – Ancient Turtle Temple  ⭐⭐  (Switches & height)
  // ────────────────────────────────────────────────────────
  {
    id: 3,
    name: 'Ancient Turtle Temple',
    subtitle: 'Unlock the secrets of the ancients!',
    stars: 2,
    worldW: 1800,
    worldH: 1440,

    background: 'temple',
    music: 'temple',

    start: { x: 200, y: 1300 },

    platforms: [
      // Ground floor
      plat(900,   1400, 1800, 40, { material: 'stone',  color: 0x78909C }),

      // Rising platforms inside the temple
      plat(300,   1220, 180, 28, { material: 'stone', color: 0x546E7A }),
      plat(600,   1080, 180, 28, { material: 'stone', color: 0x546E7A }),
      plat(900,    940, 180, 28, { material: 'stone', color: 0x546E7A }),
      plat(600,    800, 180, 28, { material: 'stone', color: 0x546E7A }),
      plat(300,    660, 180, 28, { material: 'stone', color: 0x546E7A }),
      plat(600,    520, 180, 28, { material: 'stone', color: 0x546E7A }),
      plat(900,    380, 180, 28, { material: 'stone', color: 0x546E7A }),

      // Walls
      plat(60,    720, 30, 1440, { isStatic: true, material: 'stone', color: 0x455A64 }),
      plat(1740,  720, 30, 1440, { isStatic: true, material: 'stone', color: 0x455A64 }),

      // Moving platform (bonus path)
      plat(1400,  900, 150, 26, {
        material: 'stone', color: 0x80CBC4,
        moving: { axis: 'y', range: 300, speed: 80 },
      }),

      // Top shrine
      plat(900,   220, 400, 40, { material: 'gold', color: 0xFDD835 }),
    ],

    water: [],
    windZones: [],

    hazards: [
      { x: 1000, y: 1350, w: 60, h: 50, type: 'spike' },
      { x: 1100, y: 1350, w: 60, h: 50, type: 'spike' },
      { x: 1200, y: 1350, w: 60, h: 50, type: 'spike' },
    ],

    goal: { x: 900, y: 160, type: 'star' },

    npcs: [
      {
        x: 400, y: 1350,
        type: 'turtle',
        dialog: [
          "The temple has many levels to climb!",
          "Spawn a ladder or rope to reach higher platforms.",
          "Watch out for the spikes at the base!",
        ],
      },
    ],

    hints: ['ladder', 'rope', 'spring', 'robot', 'cloud'],
    unlockMessage: 'You reached the top of the Ancient Temple!',
  },

  // ────────────────────────────────────────────────────────
  // LEVEL 4 – Volcano Island  ⭐⭐⭐  (Fire & time pressure)
  // ────────────────────────────────────────────────────────
  {
    id: 4,
    name: 'Volcano Island',
    subtitle: 'The volcano is erupting! ESCAPE!',
    stars: 3,
    worldW: 2200,
    worldH: 720,
    background: 'volcano',
    music: 'volcano',
    timelimit: 90, // seconds

    start: { x: 180, y: 540 },

    platforms: [
      // Crumbling ground (lava rises from below)
      plat(400,   660, 700, 40, { material: 'stone', color: 0x5D4037, crumbles: true }),
      plat(900,   560, 200, 30, { material: 'stone', color: 0x5D4037, crumbles: true }),
      plat(1200,  500, 200, 30, { material: 'stone', color: 0x5D4037 }),
      plat(1500,  440, 200, 30, { material: 'stone', color: 0x5D4037 }),
      plat(1800,  380, 250, 30, { material: 'stone', color: 0x5D4037 }),
      // Escape boat dock
      plat(2080,  340, 240, 40, { material: 'wood',  color: 0x8D6E63 }),
    ],

    water: [],

    windZones: [
      // Ash cloud pushes player back
      { x: 800, y: 0, w: 600, h: 720, fx: -120, fy: 0 },
    ],

    hazards: [
      // Lava pits
      { x: 660,  y: 695, w: 340, h: 80, type: 'lava' },
      { x: 1100, y: 695, w: 200, h: 80, type: 'lava' },
    ],

    risingLava: {
      startY: 900,
      speed: 20, // px/s
    },

    goal: { x: 2080, y: 280, type: 'boat_escape' },

    npcs: [
      {
        x: 300, y: 590,
        type: 'turtle',
        dialog: [
          "The volcano is erupting! The lava is rising!",
          "Freeze the lava or jump over it!",
          "Try: ice, balloon, or helicopter to escape!",
        ],
      },
    ],

    hints: ['ice', 'balloon', 'helicopter', 'trampoline', 'bridge'],
    unlockMessage: 'You escaped Volcano Island just in time!',
  },

  // ────────────────────────────────────────────────────────
  // LEVEL 5 – Sky Garden  ⭐⭐⭐  (Wind & altitude)
  // ────────────────────────────────────────────────────────
  {
    id: 5,
    name: 'Sky Garden',
    subtitle: 'Reach the floating castle!',
    stars: 3,
    worldW: 1600,
    worldH: 3000,
    background: 'sky',
    music: 'sky',

    start: { x: 800, y: 2860 },

    platforms: [
      // Ground
      plat(800,  2960, 1600, 40, { material: 'grass', color: 0x4CAF50 }),

      // First floating island cluster
      plat(500,  2600, 200, 35, { material: 'grass', color: 0x81C784 }),
      plat(900,  2500, 200, 35, { material: 'grass', color: 0x81C784 }),
      plat(1200, 2400, 200, 35, { material: 'grass', color: 0x81C784 }),

      // Cloud section (slippery!)
      plat(700,  2100, 220, 45, { material: 'cloud', color: 0xECEFF1 }),
      plat(1000, 1900, 220, 45, { material: 'cloud', color: 0xECEFF1 }),
      plat(600,  1700, 220, 45, { material: 'cloud', color: 0xECEFF1 }),

      // Moving sky platforms
      plat(900,  1400, 170, 30, {
        material: 'stone', color: 0xB2EBF2,
        moving: { axis: 'x', range: 300, speed: 100 },
      }),
      plat(700,  1100, 170, 30, {
        material: 'stone', color: 0xB2EBF2,
        moving: { axis: 'x', range: 400, speed: 130 },
      }),

      // Castle approach
      plat(800,   800, 300, 40, { material: 'stone', color: 0xCFD8DC }),
      plat(800,   600, 300, 40, { material: 'stone', color: 0xCFD8DC }),
      plat(800,   400, 300, 40, { material: 'stone', color: 0xCFD8DC }),

      // Castle floor
      plat(800,   200, 600, 50, { material: 'gold',  color: 0xFFD54F }),
    ],

    water: [],

    windZones: [
      // Strong updraft in the middle
      { x: 600,  y: 1000, w: 400, h: 1000, fx: 0, fy: -400 },
      // Side wind gusts
      { x: 0,    y: 1500, w: 300, h: 800, fx: 300, fy: 0 },
      { x: 1300, y: 1500, w: 300, h: 800, fx: -300, fy: 0 },
    ],

    hazards: [],

    goal: { x: 800, y: 130, type: 'star' },

    collectibles: [
      { x: 500,  y: 2550, type: 'gem' },
      { x: 900,  y: 2450, type: 'gem' },
      { x: 1200, y: 2350, type: 'gem' },
      { x: 700,  y: 2050, type: 'gem' },
    ],

    npcs: [
      {
        x: 900, y: 2860,
        type: 'turtle',
        dialog: [
          "The Sky Garden is SO high up!",
          "Use balloons, clouds, or a dragon to fly!",
          "Watch out for the wind gusts!",
        ],
      },
    ],

    hints: ['balloon', 'dragon', 'helicopter', 'cloud', 'giant balloon'],
    unlockMessage: 'You reached the Sky Castle! Amazing!',
  },
];

export default LEVELS;
